import { ResizableNodeView } from '@tiptap/core';
import { Image } from '@tiptap/extension-image';

/**
 * Extends the built-in Image extension to enforce a maximum resize width equal to
 * the editor's current content width, preventing images from overflowing the editor.
 *
 * During resize the aspect ratio is recalculated from the image's natural dimensions
 * so that clamping never distorts the image.
 */
export const ImageWithMaxWidth = Image.extend({
  addNodeView() {
    if (!this.options.resize || !this.options.resize.enabled || typeof document === 'undefined') {
      return null;
    }

    const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } = this.options.resize;

    return ({ node, getPos, HTMLAttributes, editor }) => {
      const el = document.createElement('img');

      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (value != null) {
          switch (key) {
            case 'width':
            case 'height':
              break;
            default:
              el.setAttribute(key, String(value));
              break;
          }
        }
      });

      el.src = HTMLAttributes.src;

      // Tracks the aspect ratio once the image has fully loaded.
      let aspectRatio = 1;

      const clampWidth = (width: number): { width: number; height: number } => {
        const maxWidth = editor.view.dom.clientWidth;
        const clampedWidth = Math.min(width, maxWidth);
        const clampedHeight = clampedWidth / aspectRatio;
        return { width: clampedWidth, height: clampedHeight };
      };

      const nodeView = new ResizableNodeView({
        element: el,
        editor,
        node,
        getPos,
        onResize: (width, _height) => {
          const { width: w, height: h } = clampWidth(width);
          el.style.width = `${w}px`;
          el.style.height = `${h}px`;
        },
        onCommit: (width, _height) => {
          const pos = getPos();
          if (pos === undefined) {
            return;
          }
          // Use offsetWidth/offsetHeight which already reflect the clamped styles
          // applied in onResize, but re-clamp defensively in case onResize was skipped.
          const { width: w, height: h } = clampWidth(el.offsetWidth);
          this.editor.chain().setNodeSelection(pos).updateAttributes(this.name, { width: w, height: h }).run();
        },
        onUpdate: (updatedNode, _decorations, _innerDecorations) => {
          if (updatedNode.type !== node.type) {
            return false;
          }
          return true;
        },
        options: {
          directions,
          min: {
            width: minWidth,
            height: minHeight,
          },
          preserveAspectRatio: alwaysPreserveAspectRatio === true,
        },
      });

      const dom = nodeView.dom as HTMLElement;
      dom.style.visibility = 'hidden';
      dom.style.pointerEvents = 'none';

      el.onload = () => {
        // Compute aspect ratio from natural dimensions once the image is available.
        if (el.naturalWidth > 0 && el.naturalHeight > 0) {
          aspectRatio = el.naturalWidth / el.naturalHeight;
        }

        // Clamp any pre-existing oversized width (e.g. images saved before this fix).
        const maxWidth = editor.view.dom.clientWidth;
        if (el.offsetWidth > maxWidth) {
          el.style.width = `${maxWidth}px`;
          el.style.height = `${maxWidth / aspectRatio}px`;
        }

        dom.style.visibility = '';
        dom.style.pointerEvents = '';
      };

      return nodeView;
    };
  },
});
